import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  v2GetPortfolioArchiveOptions,
  v2GetPortfolioQueueOptions,
  v2GetStartupOutreachOutcomesOptions,
  v2RestorePortfolioArchivedMutation,
} from '@workspace/api-backend/react-query'
import { outreachApi, outreachArchiveQueryKey, outreachQueueQueryKey } from '../api'

/**
 * Archiving takes a reply off the Outcomes page and marks it read, so the
 * outcomes payload behind that page — and the unread badge that counts the same
 * rows — has to be refetched alongside the boards.
 */
function invalidateOutcomes(queryClient: QueryClient, startupId: string) {
  return queryClient.invalidateQueries({
    queryKey: v2GetStartupOutreachOutcomesOptions({ path: { id: startupId } }).queryKey,
  })
}

/** Archived investor contacts (skipped, declined, engaged, no response). */
export function useOutreachArchiveQuery(startupId: string) {
  return useQuery({
    queryKey: outreachArchiveQueryKey(startupId),
    queryFn: () => outreachApi.getArchive(startupId),
  })
}

/** The warm-intro half of the Archive page — same buckets, portfolio founders. */
export function usePortfolioArchiveQuery(startupId: string) {
  return useQuery(v2GetPortfolioArchiveOptions({ path: { id: startupId } }))
}

/** Restore an archived portfolio founder back onto the warm-intro board. */
export function useRestorePortfolioArchivedMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2RestorePortfolioArchivedMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: v2GetPortfolioArchiveOptions({ path: { id: startupId } }).queryKey,
      })
      await queryClient.invalidateQueries({
        queryKey: v2GetPortfolioQueueOptions({ path: { id: startupId } }).queryKey,
      })
      await invalidateOutcomes(queryClient, startupId)
    },
  })
}

/** Archive a finished (no-response) contact — moves it off the board. */
export function useArchiveContactMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (leadId: string) => outreachApi.archiveContact(startupId, leadId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: outreachQueueQueryKey(startupId) })
      await queryClient.invalidateQueries({ queryKey: outreachArchiveQueryKey(startupId) })
      await invalidateOutcomes(queryClient, startupId)
    },
  })
}

/** Archive a portfolio outreach message — the warm-intro side's equivalent. */
export function useArchivePortfolioMessageMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (messageId: string) => outreachApi.archivePortfolioMessage(startupId, messageId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: v2GetPortfolioQueueOptions({ path: { id: startupId } }).queryKey,
      })
      await queryClient.invalidateQueries({
        queryKey: v2GetPortfolioArchiveOptions({ path: { id: startupId } }).queryKey,
      })
      await invalidateOutcomes(queryClient, startupId)
    },
  })
}

/** Skip every portfolio founder still awaiting a decision. */
export function useSkipAllPortfolioQueuedMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => outreachApi.skipAllPortfolioQueued(startupId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: v2GetPortfolioQueueOptions({ path: { id: startupId } }).queryKey,
      })
      await queryClient.invalidateQueries({
        queryKey: v2GetPortfolioArchiveOptions({ path: { id: startupId } }).queryKey,
      })
    },
  })
}

/** Remove an in-progress lead from HeyReach and archive it. */
export function useRemoveInProgressLeadMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (leadId: string) => outreachApi.removeInProgressLead(startupId, leadId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: outreachQueueQueryKey(startupId) })
      await queryClient.invalidateQueries({ queryKey: outreachArchiveQueryKey(startupId) })
    },
  })
}

/** Restore an archived contact back onto the board. */
export function useRestoreArchivedMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (leadId: string) => outreachApi.restoreArchived(startupId, leadId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: outreachArchiveQueryKey(startupId) })
      await queryClient.invalidateQueries({ queryKey: outreachQueueQueryKey(startupId) })
      await invalidateOutcomes(queryClient, startupId)
    },
  })
}
